/**
 * Rupees in words, Indian style.
 *
 * Every invoice in India carries the amount in words, and it is the line a
 * customer reads to check the figure. The grouping is lakh and crore, not
 * thousands and millions — "one lakh twenty thousand", never "one hundred and
 * twenty thousand".
 *
 * The input is a string because that is how money travels through this system;
 * turning it into a float here would be the one place precision was lost.
 */

const ONES = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];

const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function underHundred(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  const tens = TENS[Math.floor(n / 10)] ?? '';
  const ones = ONES[n % 10] ?? '';
  return ones ? `${tens}-${ones}` : tens;
}

function underThousand(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds === 0) return underHundred(rest);
  const head = `${ONES[hundreds]} hundred`;
  return rest === 0 ? head : `${head} and ${underHundred(rest)}`;
}

/** Splits into crore, lakh, thousand, hundred — the Indian grouping. */
function indianWords(whole: number): string {
  if (whole === 0) return 'zero';

  const parts: string[] = [];
  const crore = Math.floor(whole / 10_000_000);
  const lakh = Math.floor((whole % 10_000_000) / 100_000);
  const thousand = Math.floor((whole % 100_000) / 1000);
  const rest = whole % 1000;

  if (crore > 0) parts.push(`${indianWords(crore)} crore`);
  if (lakh > 0) parts.push(`${underThousand(lakh)} lakh`);
  if (thousand > 0) parts.push(`${underThousand(thousand)} thousand`);
  if (rest > 0) parts.push(underThousand(rest));

  return parts.join(' ');
}

const capitalise = (s: string): string => (s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1));

export function amountInWords(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '';

  const negative = amount.trim().startsWith('-');
  const [wholeText = '0', fractionText = ''] = amount.replace('-', '').split('.');
  const whole = Number(wholeText);
  // Two digits, so "50" is fifty paise rather than five.
  const paise = Number(fractionText.padEnd(2, '0').slice(0, 2));

  if (!Number.isFinite(whole) || !Number.isFinite(paise)) return '';

  const rupees = `${indianWords(whole)} rupee${whole === 1 ? '' : 's'}`;
  const withPaise = paise > 0 ? `${rupees} and ${underHundred(paise)} paise` : rupees;

  return capitalise(`${negative ? 'minus ' : ''}${withPaise} only`);
}
